import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import TableTools from "../../components/common/TableTools";
import { Heading } from "./styles";
import { exportData, tableColumns } from "./utils";
import EmptyTable from "./components/EmptyTable";
import CustomTable from "../../components/common/CustomTable";
import { StyledTransactionsTable } from "../Wallets/components/TransactionsTable/style";
import { useAppSelector } from "../../app/hooks";
import Loader from "../../components/common/Loader";
import { useWindowSize } from "@reach/window-size";
import MobileTable from "./components/MobileTable";
import { ReactComponent as EmptyCustomer } from './assets/emptyCustomer.svg';
import { selectUserRoleAndPermissions } from "../Settings/Team/redux/slice";
import { NetworkErrorAlertContext } from "../../context/NetworkErrorAlert";
import { getAllCustomers } from "../../app/queries/customers";
import { vaCurrencySelectOptions } from "../Wallets/utils";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { FEATURE_FLAG_KEYS } from "../../utils/featureFlags";

interface CustomerPayload {
    businessId: string | null;
    currency: string | null;
    pageNumber: number;
    pageSize: number;
    startDay: string | null;
    endDay: string | null;
}

interface FilterStateType {
    selectedCurrency: string | null;
    filterDate: any[];
    payload: CustomerPayload;
}

const Customers = () => {
    const navigate = useNavigate();
    const { width } = useWindowSize();
    const [searchQuery, setSearchQuery] = useState("");
    const { businessId } = useAppSelector(
        (state) => state?.users
    );
    const { onShowAlert } = useContext(NetworkErrorAlertContext);
    const { role, permissions } = useAppSelector(selectUserRoleAndPermissions);

    const [filterState, setFilterState] = useState<FilterStateType>({
        selectedCurrency: null,
        filterDate: [],
        payload: {
            businessId: businessId,
            currency: null,
            pageNumber: 1,
            pageSize: 10,
            startDay: null,
            endDay: null,
        },
    });

    const { selectedCurrency, filterDate, payload } = filterState;
    const { pageNumber, pageSize } = payload;

    const handleTablePageChange = (page: number, size: number) => {
        setFilterState({
            ...filterState,
            payload: {
                ...payload,
                pageNumber: page,
                pageSize: size,
            }
        })
    };

    const handleCurrencyChange = (value) => {
        setFilterState({
            ...filterState,
            selectedCurrency: value,
        });
    }

    const handleDateChange = (dates) => {
        setFilterState({
            ...filterState,
            filterDate: dates,
        });
    }

    const handleRowClick = (row) => {
        navigate(row?.account, {
            state: { customerDetail: row }
        });
    };

    const handleFilterProcess = () => {
        setFilterState({
            ...filterState,
            payload: {
                ...payload,
                pageNumber: 1,
                currency: selectedCurrency || null,
                startDay: filterDate[0] ? dayjs(filterDate[0]).format("YYYY-MM-DD") : null,
                endDay: filterDate[1] ? dayjs(filterDate[1]).format("YYYY-MM-DD") : null,
            }
        })
    }

    const handleResetProcess = () => {
        setFilterState({
            selectedCurrency: null,
            filterDate: [],
            payload: {
                ...payload,
                pageNumber: 1,
                currency: null,
                startDay: null,
                endDay: null,
                businessId: businessId,
            }
        });
    }

    const hasDownloadPermission = permissions?.["customers"]?.includes("EXPORT_CUSTOMER");
    
    const { data: data, isLoading } = getAllCustomers(payload)
           
    const allCustomers = data?.virtualAccounts?.map((item) => ({
        key: item.id,
        name: item.accountName,
        email: item.email || 'NA',
        account: item.accountNumber,
        currency: item.currency,
        date: dayjs(item.createdAt).format('DD.MM.YY HH:mm')
    }));

    const filteredCustomers = allCustomers?.filter(customer =>
        customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const onCSVButtonClick = async () => {
        if (hasDownloadPermission || role === "OWNER") {
            await exportData(allCustomers, "csv");
        } else {
            onShowAlert("", "Access is denied");
        }
    };

    const onXLSButtonClick = async () => {
        if (hasDownloadPermission || role === "OWNER") {
            await exportData(allCustomers, "xls");
        } else {
            onShowAlert("", "Access is denied");
        }
    };

    const enableVirtualAccountsV2 = useFeatureFlagEnabled(FEATURE_FLAG_KEYS.virtualAccountsV2);

    return (
        <>
            <Heading>Customers</Heading>
            <StyledTransactionsTable>
                {allCustomers?.length > 0 && (
                    <TableTools
                        placeHolder="Search by name or email"
                        onSearchChange={(e) =>
                            setSearchQuery(e.target.value)
                        }
                        hideFilter={!enableVirtualAccountsV2}
                        onCSVButtonClick={onCSVButtonClick}
                        onXLSButtonClick={onXLSButtonClick}
                        showSelectOptions
                        selectOptions={vaCurrencySelectOptions}
                        selectTitle="Currency"   
                        onStatusChange={handleCurrencyChange}
                        onDateChange={handleDateChange}
                        onFilterButtonClick={handleFilterProcess}
                        onResetButtonClick={handleResetProcess}
                    />
                )}

                {isLoading ? (
                    <Loader isLoading={isLoading} />
                ) : (
                    <>
                        {width > 767 ? (
                            <CustomTable
                                columns={tableColumns}
                                dataSource={filteredCustomers}
                                showEmptyImg={filteredCustomers?.length === 0}
                                icon={<EmptyCustomer />}
                                handleRowClick={handleRowClick}
                                extraEmptyChildren={<EmptyTable />}

                                currentPage={pageNumber}
                                pageSize={pageSize}
                                totalItemCount={
                                    data?.totalCount || 0
                                }
                                onPageChange={handleTablePageChange}
                            />
                        ) : (
                            <>
                                {filteredCustomers.map((customer) => (
                                    <MobileTable
                                        key={customer.key}
                                        fullName={customer.name}
                                        email={customer.email}
                                        phoneNumber={customer.phone}
                                        country="Nigeria"
                                        virtualAccount={customer.account}
                                        date={customer.date}
                                        onClick={() =>
                                            handleRowClick(customer)
                                        }
                                    />
                                ))}
                            </>
                        )}
                    </>
                )}
            </StyledTransactionsTable>
        </>
    );
};

export default Customers;
